'use client';

import { useEffect } from 'react';
import { useSetState } from 'minimal-shared/hooks';
import { onMessage, getMessaging } from 'firebase/messaging';

import {
  getData,
  updateData,
  firebaseApp,
  createBatch,
  distributedCounter,
} from 'src/lib/firebase';

import { toast } from 'src/components/snackbar';

import { useAuthContext } from 'src/auth/hooks';

import { NotificationsContext } from './notifications-context';

// ----------------------------------------------------------------------

export function NotificationsProvider({ children }) {
  const { user } = useAuthContext();

  const { state, setState, resetState } = useSetState({
    totalUnRead: 0,
    loadingMore: true,
    lastVisibleAll: null,
    allNotifications: [],
    totalNotifications: 0,
    lastVisibleUnread: null,
    unreadNotifications: [],
  });

  const getNotificationCounters = async () => {
    const { totalUnRead = 0, totalNotifications = 0 } = await getData({
      path: `notifications/${user?.id}`,
    });
    setState({ totalUnRead, totalNotifications });
  };

  const getAllNotifications = async (prevAllNotifications = [], lastVisibleAll = null) => {
    const all_notifications = await getData({
      path: `notifications/${user?.id}/messages`,
      options: {
        limit: 10,
        startAfter: lastVisibleAll,
        orderBy: [{ field: 'createdAt', value: 'desc' }],
      },
    });
    setState({
      loadingMore: false,
      lastVisibleAll: all_notifications[all_notifications.length - 1],
      allNotifications: [...prevAllNotifications, ...all_notifications],
    });
  };

  const getUnreadNotifications = async (prevUnreadNotifications = [], lastVisibleUnread = null) => {
    const unread_notifications = await getData({
      path: `notifications/${user?.id}/messages`,
      options: {
        limit: 10,
        startAfter: lastVisibleUnread,
        orderBy: [{ field: 'createdAt', value: 'desc' }],
        where: [{ field: 'isUnRead', operator: '==', value: true }],
      },
    });
    setState({
      loadingMore: false,
      lastVisibleUnread: unread_notifications[unread_notifications.length - 1],
      unreadNotifications: [...prevUnreadNotifications, ...unread_notifications],
    });
  };

  const loadMoreNotifications = (type) => {
    if (user?.id) {
      setState({ loadingMore: true });
      if (type === 'all') {
        getAllNotifications(state.allNotifications, state.lastVisibleAll);
      } else if (type === 'unread') {
        getUnreadNotifications(state.unreadNotifications, state.lastVisibleUnread);
      }
    }
  };

  const getAllNotificationsData = () => {
    if (user?.id) {
      getNotificationCounters();
      getUnreadNotifications();
      getAllNotifications();
    } else {
      resetState();
    }
  };

  const handleMarkAllAsRead = async () => {
    if (user?.id) {
      const unreadNotifications = await getData({
        path: `notifications/${user?.id}/messages`,
        options: { where: [{ field: 'isUnRead', operator: '==', value: true }] },
      });
      const batch = createBatch();
      unreadNotifications.forEach(({ id }) => {
        updateData(`notifications/${user?.id}/messages/${id}`, { isUnRead: false }, batch);
      });
      updateData(`notifications/${user?.id}`, { totalUnRead: 0 }, batch);
      await batch.commit();
      getAllNotificationsData();
    }
  };

  const handleMarkAsRead = async (id) => {
    if (user?.id) {
      const batch = createBatch();
      distributedCounter(`notifications/${user?.id}`, 'totalUnRead', -1, batch);
      updateData(`notifications/${user?.id}/messages/${id}`, { isUnRead: false }, batch);
      await batch.commit();
      getAllNotificationsData();
    }
  };

  useEffect(() => {
    getAllNotificationsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(
    () =>
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      onMessage(getMessaging(firebaseApp), (payload) => {
        if (payload?.notification?.title) {
          getAllNotificationsData();
          toast(
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ marginRight: '16px' }}>
                <img
                  alt="Mestore"
                  src={payload?.notification?.icon}
                  style={{ height: '50px', width: '60px' }}
                />
              </div>
              <div>
                <strong>{payload?.notification?.title}</strong>
                <div>{payload?.notification?.body}</div>
              </div>
            </div>
          );
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const contextValues = {
    ...state,
    disabled: !user?.id,
    //
    handleMarkAsRead,
    handleMarkAllAsRead,
    loadMoreNotifications,
  };

  return <NotificationsContext value={contextValues}>{children}</NotificationsContext>;
}
